import { useEffect, useRef, useState, type SVGProps } from "react";
import { ArrowLeft, CirclePlus, ExternalLink, Trash2 } from "lucide-react";
import duckArchiveGif from "../../../assets/utya-archive.gif";
import {
  RESOURCE_STATUS_LABELS,
  RESOURCE_TYPE_LABELS,
  labelFrom,
} from "../../../lib/labels";
import { withInteractiveReadTimeout } from "../../../lib/requestTimeout";
import { openTelegramLink } from "../../../lib/tg";
import {
  driverApi,
  type OwnResourcePatch,
  type OwnResourceType,
} from "../lib/api";
import {
  ResourceWizard,
  type ResourceWizardApi,
} from "../../sponsor/pages/ResourceWizard";

type OwnResource = {
  id: number;
  name: string;
  type: OwnResourceType;
  url: string;
  target_action: "subscribe" | "join_request" | "join_confirmed" | "start_bot";
  bot_launch_mode?: "start" | "unique_start" | null;
  verification_mode: "verified" | "no_check";
  status: string;
  open_url?: string | null;
};

type View =
  | { kind: "list" }
  | { kind: "create" }
  | { kind: "edit"; resourceId: number }
  | { kind: "view"; resourceId: number };

const ownResourceWizardApi: ResourceWizardApi = {
  getResource: (id, signal) => driverApi.getOwnResource(id, signal),
  createResource: (body) =>
    driverApi.createOwnResource({
      name: body.name,
      type: body.type as OwnResourceType,
    }),
  patchResource: (id, body) =>
    driverApi.patchOwnResource(id, body as OwnResourcePatch),
  verifyResource: (id) => driverApi.verifyOwnResource(id),
  submitResource: (id) => driverApi.submitOwnResource(id),
};

export function OwnResources() {
  const [view, setView] = useState<View>({ kind: "list" });
  const [items, setItems] = useState<OwnResource[] | null>(null);
  const [maxOwnResources, setMaxOwnResources] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const loadGeneration = useRef(0);

  async function load() {
    const generation = ++loadGeneration.current;
    setItems(null);
    setMaxOwnResources(null);
    setError(null);
    try {
      const [resourcesResult, rulesResult] = await withInteractiveReadTimeout(
        (signal) =>
          Promise.all([
            driverApi.listOwnResources(undefined, signal),
            driverApi.getOwnResourceRules(signal),
          ]),
      );
      if (generation !== loadGeneration.current) return;
      const resources = Array.isArray(resourcesResult.data)
        ? (resourcesResult.data as OwnResource[])
        : null;
      const configuredLimit = (
        rulesResult.data as
          | { max_own_resources_per_driver?: unknown }
          | null
          | undefined
      )?.max_own_resources_per_driver;
      const limit =
        typeof configuredLimit === "number" &&
        Number.isInteger(configuredLimit) &&
        configuredLimit >= 0
          ? configuredLimit
          : null;

      setItems(resources ?? []);
      setMaxOwnResources(limit);
      if (resources === null) {
        setError("не удалось загрузить личные ресурсы");
      } else if (limit === null) {
        setError("не удалось загрузить лимит личных ресурсов");
      }
    } catch {
      if (generation === loadGeneration.current) {
        setItems([]);
        setMaxOwnResources(null);
        setError("не удалось загрузить личные ресурсы и лимит");
      }
    }
  }

  useEffect(() => {
    void load();
    return () => {
      loadGeneration.current += 1;
    };
  }, []);

  function backToList() {
    setView({ kind: "list" });
    void load();
  }

  function openResource(resource: OwnResource) {
    const url = resource.open_url || resource.url;
    if (!url) return;
    setConfirmDeleteId(null);
    openTelegramLink(url);
  }

  async function deleteResource(resource: OwnResource) {
    if (confirmDeleteId !== resource.id) {
      setConfirmDeleteId(resource.id);
      return;
    }
    setDeletingId(resource.id);
    setError(null);
    try {
      const r = await driverApi.deleteOwnResource(resource.id);
      if (r.error) {
        const detail = (r.error as { detail?: string }).detail;
        setError(detail ?? "не удалось удалить личный ресурс");
        return;
      }
      setItems((current) =>
        (current ?? []).filter((item) => item.id !== resource.id),
      );
    } catch {
      setError("не удалось удалить личный ресурс");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }

  async function toggleResource(resource: OwnResource) {
    const enabled = isOwnResourceEnabled(resource);
    if (!isOwnResourceToggleAvailable(resource) || togglingId !== null) return;
    setTogglingId(resource.id);
    setError(null);
    try {
      const r = await driverApi.patchOwnResourceSettings(resource.id, {
        enabled: !enabled,
      });
      if (r.error || !r.data) {
        const detail = (r.error as { detail?: string })?.detail;
        setError(detail ?? "не удалось изменить статус ресурса");
        return;
      }
      const updated = r.data as OwnResource;
      setItems((current) =>
        (current ?? []).map((item) =>
          item.id === updated.id ? updated : item,
        ),
      );
    } catch {
      setError("не удалось изменить статус ресурса");
    } finally {
      setTogglingId(null);
    }
  }

  if (view.kind === "create") {
    return (
      <ResourceWizard
        key="driver-own-resource-create"
        api={ownResourceWizardApi}
        mode="create"
        pricingMode="free"
        onDone={backToList}
        onCancel={backToList}
      />
    );
  }

  if (view.kind === "edit") {
    return (
      <ResourceWizard
        key={`driver-own-resource-edit-${view.resourceId}`}
        api={ownResourceWizardApi}
        mode="edit"
        resourceId={view.resourceId}
        pricingMode="free"
        onDone={backToList}
        onCancel={backToList}
      />
    );
  }

  if (view.kind === "view") {
    return (
      <OwnResourceDetails resourceId={view.resourceId} onBack={backToList} />
    );
  }

  const visibleItems = (items ?? []).filter((item) => item.status !== "draft");
  const limitReached =
    maxOwnResources !== null && visibleItems.length >= maxOwnResources;
  const isEmpty = visibleItems.length === 0;

  if (items === null && !error) {
    return (
      <div className="tp-screen tp-own-resources-screen">
        <div className="tp-card-soft text-sm text-muted-foreground">
          загрузка...
        </div>
      </div>
    );
  }

  return (
    <div className="tp-screen tp-own-resources-screen">
      <div className="tp-driver-stats-hero tp-own-resources-hero">
        <img
          src={duckArchiveGif}
          alt=""
          aria-hidden="true"
          className="tp-driver-stats-duck"
          draggable={false}
        />
        <h1 className="tp-driver-stats-title">личные ресурсы</h1>
      </div>

      <button
        className="tp-button-primary tp-own-resources-add-button w-full disabled:cursor-not-allowed disabled:opacity-50"
        disabled={items === null || maxOwnResources === null || limitReached}
        onClick={() => setView({ kind: "create" })}
      >
        <CirclePlus className="h-5 w-5" aria-hidden="true" />
        <span>
          {maxOwnResources === null
            ? "лимит недоступен"
            : limitReached
              ? `достигнут лимит: ${maxOwnResources}`
              : "добавить ресурс"}
        </span>
      </button>

      {error ? (
        <div className="tp-alert" role="alert">
          <div>{error}</div>
          <button
            className="tp-button-secondary mt-3"
            onClick={() => void load()}
          >
            повторить
          </button>
        </div>
      ) : null}

      {!isEmpty ? (
        <ul className="tp-own-resources-list">
          {visibleItems.map((item) => {
            const enabled = isOwnResourceEnabled(item);
            const canToggle = isOwnResourceToggleAvailable(item);
            return (
              <li key={item.id} className="tp-card space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-base font-semibold">
                      {item.name}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {labelFrom(RESOURCE_TYPE_LABELS, item.type)}
                    </div>
                  </div>
                  <span className="tp-chip shrink-0">
                    {labelFrom(RESOURCE_STATUS_LABELS, item.status)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className="tp-button-secondary col-span-2 w-full gap-2"
                    disabled={!(item.open_url || item.url)}
                    onClick={(event) => {
                      event.stopPropagation();
                      openResource(item);
                    }}
                  >
                    <ExternalLink
                      className="h-[1.05rem] w-[1.05rem]"
                      aria-hidden="true"
                    />
                    <span>открыть</span>
                  </button>
                  <button
                    className="tp-button-secondary tp-own-resource-power-button gap-2"
                    data-enabled={enabled}
                    disabled={!canToggle || togglingId === item.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      setConfirmDeleteId(null);
                      void toggleResource(item);
                    }}
                  >
                    <PowerIcon aria-hidden="true" />
                    <span>
                      {togglingId === item.id
                        ? "сохраняем..."
                        : enabled
                          ? "включен"
                          : "выключен"}
                    </span>
                  </button>
                  <button
                    className="tp-button-secondary gap-2"
                    disabled={deletingId === item.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      void deleteResource(item);
                    }}
                  >
                    <Trash2
                      className="h-[1.05rem] w-[1.05rem]"
                      aria-hidden="true"
                    />
                    <span>
                      {deletingId === item.id
                        ? "удаляем..."
                        : confirmDeleteId === item.id
                          ? "подтвердить"
                          : "удалить"}
                    </span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function isOwnResourceEnabled(resource: OwnResource): boolean {
  return resource.status === "active" || resource.status === "approved";
}

function isOwnResourceToggleAvailable(resource: OwnResource): boolean {
  return ["approved", "active", "paused"].includes(resource.status);
}

function PowerIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      className={`h-[1.05rem] w-[1.05rem] ${props.className ?? ""}`.trim()}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M7 6a7.75 7.75 0 1 0 10 0" />
      <path d="M12 4v8" />
    </svg>
  );
}

function OwnResourceDetails({
  resourceId,
  onBack,
}: {
  resourceId: number;
  onBack: () => void;
}) {
  const [resource, setResource] = useState<OwnResource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [retryGeneration, setRetryGeneration] = useState(0);

  useEffect(() => {
    let active = true;
    setResource(null);
    setError(null);
    void withInteractiveReadTimeout((signal) =>
      driverApi.getOwnResource(resourceId, signal),
    )
      .then((r) => {
        if (!active) return;
        if (r.data) setResource(r.data as OwnResource);
        else setError("не удалось открыть ресурс");
      })
      .catch(() => {
        if (active) setError("не удалось открыть ресурс");
      });
    return () => {
      active = false;
    };
  }, [resourceId, retryGeneration]);

  if (error) {
    return (
      <div className="tp-screen">
        <div className="tp-alert">{error}</div>
        <button
          className="tp-button-secondary"
          onClick={() => setRetryGeneration((value) => value + 1)}
        >
          повторить
        </button>
        <button
          className="tp-button-primary"
          onClick={onBack}
          aria-label="назад"
        >
          ←
        </button>
      </div>
    );
  }

  if (!resource) {
    return <div className="tp-screen">загрузка...</div>;
  }

  const rows = [
    ["тип ресурса", labelFrom(RESOURCE_TYPE_LABELS, resource.type)],
    ["статус", labelFrom(RESOURCE_STATUS_LABELS, resource.status)],
    ["ссылка", resource.url || "-"],
  ];
  const settingsAvailable = ["approved", "active", "paused"].includes(
    resource.status,
  );
  const resourceEnabled =
    resource.status === "active" || resource.status === "approved";

  async function saveEnabled(enabled: boolean) {
    if (!resource || saving) return;
    setSaving(true);
    setSettingsError(null);
    try {
      const r = await driverApi.patchOwnResourceSettings(resource.id, {
        enabled,
      });
      if (r.error || !r.data) {
        const detail = (r.error as { detail?: string })?.detail;
        setSettingsError(detail ?? "не удалось сохранить настройки");
        return;
      }
      setResource(r.data as OwnResource);
    } catch {
      setSettingsError("не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tp-screen tp-resource-settings-screen">
      <div className="tp-resource-settings-header">
        <button
          type="button"
          className="tp-resource-settings-back"
          aria-label="назад"
          onClick={onBack}
        >
          <ArrowLeft size={20} strokeWidth={2.2} />
        </button>
        <h1 className="tp-resource-settings-page-title">
          настройки {resource.name}
        </h1>
        <span aria-hidden="true" />
      </div>

      <div className="tp-resource-settings-card tp-resource-settings-info">
        {rows.map(([label, value]) => (
          <div key={label} className="tp-resource-settings-info-row">
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="tp-resource-settings-card">
        {settingsError ? <div className="tp-alert">{settingsError}</div> : null}
        <label className="tp-resource-settings-toggle">
          <span>включить ресурс</span>
          <input
            type="checkbox"
            className="tp-resource-settings-toggle-input"
            checked={resourceEnabled}
            disabled={saving || !settingsAvailable}
            onChange={(event) => saveEnabled(event.target.checked)}
          />
          <span
            className="tp-resource-settings-toggle-track"
            aria-hidden="true"
          >
            <span />
          </span>
        </label>
      </div>
    </div>
  );
}
